import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  IconButton,
  SlideFade,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Spinner,
  Text,
  Textarea,
  Tooltip,
} from '@chakra-ui/react'
import { Field, FieldProps, Formik } from 'formik'
import { useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IoIosClose } from 'react-icons/io'
import Select from 'react-select'
import useFetch, { CachePolicies } from 'use-http'
import * as Yup from 'yup'
import { PermissionsEnum } from '../../../../utils'
import {
  GenerateImageErrorResponse,
  GenerateImageResponse,
} from '../../../interfaces'
import { MLContext, UserContext } from '../../../providers'
import { MediaLibraryActions } from '../../../providers/MLProvider'
import ExampleImagePreview from './ExampleImagePreview'
import ImageGallery from './ImageGallery'

interface FormProps {
  prompt: string
  dimension: {
    label: string
    value: string
  }
  numImages: number
}

interface PromptFieldAndSubmitProps {
  handleChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void
  isDisabled: boolean
  isSubmitting: boolean
  submitForm: () => Promise<any>
  setIsOpenAdvancedSettings: (value: boolean) => void
  isOpenAdvancedSettings: boolean
}

interface AdvancedSettingsProps {
  isOpenAdvancedSettings: boolean
  setIsOpenAdvancedSettings: (value: boolean) => void
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  values: FormProps
}

interface ExamplePromptsProps {
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
}

const dimensionOptions = [
  { label: '512 x 512', value: '512 x 512' },
  { label: '1024 x 1024', value: '1024 x 1024' },
  { label: '1152 x 896', value: '1152 x 896' },
  { label: '1216 x 832', value: '1216 x 832' },
  { label: '1344 x 768', value: '1344 x 768' },
  { label: '768 x 1344', value: '768 x 1344' },
]

const dimensionMap = {
  '512 x 512': {
    height: 512,
    width: 512,
  },
  '1024 x 1024': {
    height: 1024,
    width: 1024,
  },
  '1152 x 896': {
    height: 896,
    width: 1152,
  },
  '1216 x 832': {
    height: 832,
    width: 1216,
  },
  '1344 x 768': {
    height: 768,
    width: 1344,
  },
  '768 x 1344': {
    height: 1344,
    width: 768,
  },
}

const LoadingToast = () => {
  const { t } = useTranslation()

  return (
    <Flex
      justify="space-between"
      position="absolute"
      bottom="32px"
      left="0"
      right="0"
      mx="auto"
      w="160px"
      h="40px"
      p="10px"
      align="center"
      borderRadius="8px"
      boxShadow="0 0 16px 0 rgba(0, 0, 0, 0.16)"
      bg="white"
    >
      <Spinner size="sm" color="appClearBlue" />
      <Text fontSize="12px">{t('generating_images', 'Generating images')}</Text>
    </Flex>
  )
}

const PromptFieldAndSubmit = ({
  handleChange,
  isSubmitting,
  isDisabled,
  submitForm,
  setIsOpenAdvancedSettings,
  isOpenAdvancedSettings,
}: PromptFieldAndSubmitProps) => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)

  return (
    <Box p="24px" width="100%">
      <Field name="prompt">
        {({ field, form }: FieldProps<string, FormProps>) => (
          <Flex
            direction="column"
            bg="white"
            p="14px"
            gap="12px"
            borderRadius="8px"
            width="100%"
            onKeyDown={e => {
              if (e.key === 'Enter' && e.ctrlKey) {
                submitForm()
              }
            }}
          >
            <FormControl
              isInvalid={Boolean(form.errors.prompt && form.touched.prompt)}
              isRequired
            >
              <FormLabel htmlFor="prompt" fontSize="16px" fontWeight="600">
                {t('prompt', 'Prompt')}
              </FormLabel>
              <Flex gap="8px">
                <Box width="100%">
                  <Textarea
                    {...field}
                    isDisabled={
                      !user?.permissions.includes(
                        PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
                      )
                    }
                    id="prompt"
                    onChange={handleChange}
                    rows={2}
                  />
                  <FormErrorMessage>{form.errors.prompt}</FormErrorMessage>
                </Box>

                <Tooltip
                  label={t(
                    'sufficient_permission_msg',
                    'You do not have sufficient permission for this operation.',
                  )}
                  isDisabled={user?.permissions.includes(
                    PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
                  )}
                  shouldWrapChildren
                >
                  <Button
                    isDisabled={
                      isDisabled ||
                      field.value.trim() === '' ||
                      isSubmitting ||
                      !user?.permissions.includes(
                        PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
                      )
                    }
                    isLoading={isSubmitting}
                    onClick={submitForm}
                  >
                    {t('generate', 'Generate')}
                  </Button>
                </Tooltip>
              </Flex>
            </FormControl>

            <Button
              variant="link"
              alignSelf="start"
              color="appClearBlue"
              onClick={() => {
                setIsOpenAdvancedSettings(!isOpenAdvancedSettings)
              }}
            >
              {t('advanced_settings', 'Advanced settings')}
            </Button>
          </Flex>
        )}
      </Field>
    </Box>
  )
}

const AdvancedSettings = ({
  isOpenAdvancedSettings,
  setIsOpenAdvancedSettings,
  setFieldValue,
  values,
}: AdvancedSettingsProps) => {
  const { t } = useTranslation()
  const [{ generateImageNumber }, dispatch] = useContext(MLContext)

  return (
    <SlideFade in={isOpenAdvancedSettings} unmountOnExit>
      <Flex
        width="20vw"
        direction="column"
        bg="white"
        p="16px"
        gap="28px"
        borderLeft="solid 1px #E6E7EA"
      >
        <Flex justify="space-between" width="100%">
          <Text fontWeight="500">
            {t('advanced_settings', 'Advanced settings')}
          </Text>
          <IconButton
            variant="link"
            aria-label={t('close', 'Close')}
            minWidth="20px"
            icon={<IoIosClose size="20px" color="#6C7686" />}
            onClick={() => {
              setIsOpenAdvancedSettings(false)
            }}
          />
        </Flex>
        <Field>
          {({ form }: FieldProps<number, FormProps>) => (
            <FormControl
              isInvalid={Boolean(
                form.errors.numImages && form.touched.numImages,
              )}
              isRequired
            >
              <FormLabel htmlFor="numImages">
                {t('number_of_images', 'Number of images')}
              </FormLabel>
              <Flex>
                <Slider
                  focusThumbOnChange={false}
                  name="numImages"
                  aria-label={t('number_of_images', 'Number of images')}
                  defaultValue={generateImageNumber}
                  min={1}
                  max={10}
                  onChange={value => {
                    setFieldValue('numImages', value)
                    dispatch({
                      type: MediaLibraryActions.SET_GENERATED_IMAGES_NUMBER,
                      payload: value,
                    })
                  }}
                >
                  <SliderTrack>
                    <SliderFilledTrack />
                  </SliderTrack>
                  <SliderThumb />
                </Slider>
                <Flex
                  width="40px"
                  height="22px"
                  p="2px 8px"
                  bg="#edf2f7"
                  ml="16px"
                  borderRadius="5.3px"
                  justify="center"
                >
                  {values.numImages}
                </Flex>
              </Flex>
              <FormErrorMessage>{form.errors.numImages}</FormErrorMessage>
            </FormControl>
          )}
        </Field>
        <Field>
          {({ form }) => (
            <FormControl
              isInvalid={Boolean(
                form.errors.dimension && form.touched.dimension,
              )}
              isRequired
            >
              <FormLabel htmlFor="dimension">
                {t('image_dimensions', 'Image dimensions')}
              </FormLabel>
              <Select
                inputId="dimension"
                options={dimensionOptions}
                value={values.dimension}
                onChange={option => {
                  setFieldValue('dimension', option)
                  if (option) {
                    dispatch({
                      type: MediaLibraryActions.SET_GENERATED_IMAGES_DIMENSION,
                      payload: option,
                    })
                  }
                }}
              />
              <FormErrorMessage>{form.errors.dimension}</FormErrorMessage>
            </FormControl>
          )}
        </Field>
      </Flex>
    </SlideFade>
  )
}

const ExamplePrompts = ({ setFieldValue }: ExamplePromptsProps) => {
  const promptList = [
    {
      prompt:
        "Deep indigos and swirling purples dominate, echoing Van Gogh's style. Stars twinkle in whites and golds against the cosmic canvas. Luminous clouds dance, and a crescent moon adds a soft radiance. Muted landscapes, rolling hills, and silhouetted trees invite quiet contemplation of the serene nocturnal beauty.",
      src: 'https://ik.imagekit.io/ikmedia/genimg/image1.jpg',
      rowSpan: 3,
      colSpan: 2,
    },
    {
      prompt:
        'Kyoto Hanami in heavy paint of soft pinks and whites. Capture the dance of sunlight through the petals.',
      src: 'https://ik.imagekit.io/ikmedia/genimg/image2.jpg',
      rowSpan: 2,
      colSpan: 1,
    },
    {
      prompt: 'Castle at the shore surrounded by water.',
      src: 'https://ik.imagekit.io/ikmedia/genimg/image5.jpg',
      rowSpan: 1,
      colSpan: 1,
    },
    {
      prompt: 'Green plant on the door of a green car.',
      src: 'https://ik.imagekit.io/ikmedia/genimg/image4.jpg',
      rowSpan: 2,
      colSpan: 1,
    },
    {
      prompt:
        'Pristine clay cup resting on a clean surface, with stack on beige plates and subtle light and shadow.',
      src: 'https://ik.imagekit.io/ikmedia/genimg/image3.jpg',
      rowSpan: 1,
      colSpan: 1,
    },
  ]

  return (
    <Grid
      templateRows="repeat(3, 0.75fr)"
      templateColumns="repeat(4, 1fr)"
      gap={4}
      p={6}
    >
      {promptList.map((prompt, index) => (
        <GridItem
          key={`image${index + 1}`}
          rowSpan={prompt.rowSpan}
          colSpan={prompt.colSpan}
        >
          <ExampleImagePreview
            prompt={prompt.prompt}
            setFieldValue={setFieldValue}
            src={prompt.src}
          />
        </GridItem>
      ))}
    </Grid>
  )
}

const GenerateImageForm = () => {
  const [isOpenAdvancedSettings, setIsOpenAdvancedSettings] = useState(false)
  const [generatedImages, setGeneratedImages] = useState<
    GenerateImageResponse[]
  >([])
  const [imageLoadStates, setImageLoadStates] = useState<boolean[]>([])
  const { user } = useContext(UserContext)
  const [{ generateImageDimension, generateImageNumber }] =
    useContext(MLContext)
  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const handleImageLoaded = (index: number, loaded: boolean) => {
    setImageLoadStates(prev => {
      const newLoadStates = [...prev]
      newLoadStates[index] = loaded
      return newLoadStates
    })
  }

  const allImagesLoaded = useMemo(() => {
    return (
      imageLoadStates.length === generatedImages.length &&
      imageLoadStates.every(loaded => loaded)
    )
  }, [imageLoadStates])

  return (
    <Flex width="100%" height="100%">
      <Formik<FormProps>
        initialValues={{
          prompt: '',
          dimension: generateImageDimension,
          numImages: generateImageNumber,
        }}
        validationSchema={Yup.object().shape({
          prompt: Yup.string().required('Required'),
          numImages: Yup.number()
            .required('Required')
            .min(1, 'Minimum 1')
            .max(10, 'Maximum 10'),
          dimension: Yup.object().shape({
            label: Yup.string().oneOf(dimensionOptions.map(o => o.label)),
            value: Yup.string()
              .oneOf(
                dimensionOptions.map(o => o.value),
                'Invalid option selected',
              )
              .required('Dimensions are required'),
          }),
        })}
        onSubmit={async values => {
          const res = (await request.post(
            `/api/media-library/${user?.clientNumber}/generate-image`,
            {
              prompt: values.prompt,
              height: dimensionMap[values.dimension.value].height,
              width: dimensionMap[values.dimension.value].width,
              numImages: values.numImages,
            },
          )) as GenerateImageResponse[] | GenerateImageErrorResponse

          if (request.error) {
          } else {
            setGeneratedImages(res as GenerateImageResponse[])
          }
        }}
      >
        {({
          handleChange,
          isSubmitting,
          submitForm,
          values,
          setFieldValue,
        }) => (
          <>
            <Flex
              position="relative"
              backgroundColor="#F2F4F7"
              backgroundImage="radial-gradient(#D6D9DC 1px, transparent 1px)"
              backgroundSize="20px 20px"
              direction="column"
              align="center"
              width="100%"
              height="100%"
              overflow="auto"
            >
              <PromptFieldAndSubmit
                {...{
                  handleChange,
                  isSubmitting,
                  isDisabled: !allImagesLoaded,
                  submitForm,
                  setIsOpenAdvancedSettings,
                  isOpenAdvancedSettings,
                }}
              />
              {generatedImages.length === 0 ? (
                <ExamplePrompts {...{ setFieldValue }} />
              ) : (
                <Box
                  m="0 24px 24px 24px"
                  p="16px"
                  borderRadius="8px"
                  bg="white"
                  w="calc(100% - 48px)"
                >
                  <ImageGallery
                    {...{
                      generatedImages,
                      handleImageLoaded,
                      preview: 'generate',
                    }}
                  />
                </Box>
              )}
              {!allImagesLoaded && <LoadingToast />}
            </Flex>
            <AdvancedSettings
              {...{
                isOpenAdvancedSettings,
                setIsOpenAdvancedSettings,
                setFieldValue,
                values,
              }}
            />
          </>
        )}
      </Formik>
    </Flex>
  )
}

export default GenerateImageForm
